/**
 * The trend beside a figure (REQ-021 AC-003, AC-005).
 *
 * A sparkline, deliberately: the executive screen is ten figures on one screen,
 * and ten plotted charts would be a different screen with the same name. The
 * mark carries shape only — every number a reader might act on is on the tile
 * beside it or in the caption below it, so nothing here is reachable by hover
 * alone.
 *
 * Three things this component refuses to draw, each for the same reason the
 * backend keeps absent days in place rather than dropping them:
 *
 * - **A line across a gap.** A day the source was gone is drawn as a break in
 *   the stroke over a shaded band. Joining the days either side would assert a
 *   value for a day nobody measured, and it would do it in the most persuasive
 *   form available — a smooth line. A day with no row at all — the snapshot job
 *   did not run — is drawn the same way and counted separately, because a
 *   missing row is the absence that leaves no trace anywhere else.
 * - **A line across a thin day.** A day that ran under its denominator is
 *   neither a reading nor an absence, and gets its own mark rather than being
 *   folded into either.
 * - **One line through two definitions.** Where `definedOn` moves mid-series
 *   the axis carries two different measures, and the boundary is ruled and
 *   named. A series whose meaning changed and cannot say so is worse than two
 *   shorter ones.
 *
 * Colours are Tailwind's, which are the palette this screen already uses:
 * sky-600 `#0376b4` for the series and amber-700 `#b45309` for a thin day pass
 * the CVD and contrast checks against the white tile as a pair; the gap band and
 * the definition rule are chrome greys, not data colours. Nothing is encoded by
 * colour alone — the caption states every count in words.
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import apiClient from "@/services/api/apiClient";
import { formatValue, UNMEASURED_SOURCE, type MetricReading } from "./metrics";
import {
  bandWidth,
  MALFORMED_TREND,
  MIN_TREND_POINTS,
  readTrend,
  SPARK,
  TREND_DAYS,
  trendFailure,
  xAt,
  yAt,
  type TrendMeta,
  type TrendPayload,
  type TrendShape,
} from "./trend";

const note = "mt-1 text-xs";

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? "" : "s"}`;

/** What the breaks in the stroke mean, in words rather than in shading. */
function breaksSentence(shape: TrendShape): string | null {
  if (shape.gaps > 0 && shape.thin > 0) {
    return `Not joined across ${plural(
      shape.gaps,
      "day",
    )} the source was gone, or ${plural(
      shape.thin,
      "day",
    )} with too few records.`;
  }
  if (shape.gaps > 0) {
    return `Not joined across ${plural(
      shape.gaps,
      "day",
    )} the source was gone.`;
  }
  if (shape.thin > 0) {
    return `Not drawn on ${plural(
      shape.thin,
      "day",
    )} with too few records to count as a reading.`;
  }
  return null;
}

/**
 * The other absence, kept in its own sentence because it has its own owner: a
 * day the source was gone was still looked at, and a day with no row was not.
 */
function unrecordedSentence(shape: TrendShape): string | null {
  if (shape.unrecorded === 0) return null;
  return `No snapshot was written on ${plural(
    shape.unrecorded,
    "day",
  )} — the job did not run, so nothing at all is known about ${
    shape.unrecorded === 1 ? "that day" : "those days"
  }.`;
}

/** Every day that is not a reading, counted in the label as in the caption. */
function absencesPhrase(shape: TrendShape): string {
  const total = shape.gaps + shape.thin + shape.unrecorded;
  const parts = [
    shape.gaps > 0 ? `${plural(shape.gaps, "day")} the source was gone` : null,
    shape.thin > 0 ? `${plural(shape.thin, "day")} with too few records` : null,
    shape.unrecorded > 0
      ? `${plural(shape.unrecorded, "day")} with no snapshot written`
      : null,
  ].filter((part): part is string => part !== null);

  const counted = `${plural(total, "day")} not measured`;
  return parts.length > 0 ? `${counted} (${parts.join(", ")})` : counted;
}

function Sparkline({
  shape,
  metric,
}: {
  shape: TrendShape;
  metric: MetricReading;
}) {
  const count = shape.span;
  const band = bandWidth(count);
  const last = shape.segments.at(-1)?.at(-1);

  return (
    <svg
      role="img"
      aria-label={`Trend over ${plural(count, "day")}: ${plural(
        shape.measured,
        "day",
      )} measured, high ${formatValue(shape.max, metric.unit)}, low ${formatValue(
        shape.min,
        metric.unit,
      )}, ${absencesPhrase(shape)}.`}
      viewBox={`0 0 ${SPARK.width} ${SPARK.height}`}
      className="h-8 w-[140px]"
    >
      {shape.days.map((point, index) =>
        point.value.status === "ok" ? null : (
          <g key={point.date}>
            {/* The band says "this column is not a value". It is drawn under
                the stroke so a break in the line sits inside it. A day with no
                row is banded like a day the source was gone, in a darker grey:
                the two are different facts, and neither is a value. */}
            <rect
              data-testid={
                point.value.status === "unavailable"
                  ? "trend-gap"
                  : point.value.status === "unrecorded"
                    ? "trend-unrecorded"
                    : "trend-thin-day"
              }
              x={xAt(index, count) - band / 2}
              y={0}
              width={band}
              height={SPARK.height}
              className={
                point.value.status === "unavailable"
                  ? "fill-gray-200"
                  : point.value.status === "unrecorded"
                    ? "fill-gray-400"
                    : "fill-amber-200"
              }
            />
            {point.value.status === "insufficient_sample" && (
              // A square on the floor, not a point on the scale: a thin day has
              // no height, and anything drawn at a height would claim one.
              <rect
                x={xAt(index, count) - 2}
                y={SPARK.height - 5}
                width={4}
                height={4}
                className="fill-amber-700"
              />
            )}
          </g>
        ),
      )}

      {shape.segments.map((segment) => {
        const first = segment[0];
        if (!first) return null;

        // A run of one measured day between two absences is a dot. Drawing it
        // as a line would need a second point, and the only points available
        // are the days that were not measured.
        return segment.length === 1 ? (
          <circle
            key={first.index}
            cx={xAt(first.index, count)}
            cy={yAt(first.value, shape.min, shape.max)}
            r={2}
            className="fill-sky-600"
          />
        ) : (
          <path
            key={first.index}
            d={segment
              .map(
                (mark, position) =>
                  `${position === 0 ? "M" : "L"} ${xAt(mark.index, count)} ${yAt(
                    mark.value,
                    shape.min,
                    shape.max,
                  )}`,
              )
              .join(" ")}
            fill="none"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="stroke-sky-600"
          />
        );
      })}

      {shape.changeIndex !== undefined && (
        <line
          data-testid="trend-definition-rule"
          x1={xAt(shape.changeIndex, count) - band / 2}
          x2={xAt(shape.changeIndex, count) - band / 2}
          y1={0}
          y2={SPARK.height}
          strokeWidth={1}
          className="stroke-gray-600"
        />
      )}

      {last !== undefined && (
        <circle
          cx={xAt(last.index, count)}
          cy={yAt(last.value, shape.min, shape.max)}
          r={3.5}
          strokeWidth={2}
          className="fill-sky-600 stroke-white"
        />
      )}
    </svg>
  );
}

export default function MetricTrend({ metric }: { metric: MetricReading }) {
  /**
   * A metric with no writer has a series of gaps and nothing else, by
   * construction. Not asking for it is one fewer request and one fewer way to
   * draw a row of empty days as though it were history.
   */
  const unmeasured = metric.source === UNMEASURED_SOURCE;

  /**
   * One request per figure, and up to ten on a load.
   *
   * `/trend` answers about one metric, so a batch would mean changing a shipped
   * contract for a screen only staff open. Ten thirty-day series are small, they
   * are cached for the session, and — the reason that settles it — they fail
   * independently: one unreadable series greys out one sparkline instead of
   * taking the other nine figures' trends with it.
   */
  const query = useQuery({
    queryKey: ["dashboard", "trend", metric.id],
    queryFn: async () => {
      const response = await apiClient.get<{
        data: TrendPayload;
        meta: TrendMeta;
      }>(
        `/staff/dashboard/trend?metric=${encodeURIComponent(metric.id)}&days=${TREND_DAYS}`,
      );
      return response.data;
    },
    enabled: !unmeasured,
    retry: false,
    // The series gains a point a day. Refetching it every five minutes with the
    // figures would be ten requests an hour for a number that cannot move.
    staleTime: 30 * 60_000,
  });

  if (unmeasured) {
    return (
      <p
        data-testid={`trend-${metric.id}`}
        className={`${note} text-amber-800`}
      >
        No source writes this metric, so there is no series to draw — not even a
        flat one.
      </p>
    );
  }

  if (query.isLoading) {
    return (
      <p
        data-testid={`trend-${metric.id}`}
        className={`${note} flex items-center gap-1.5 text-gray-500`}
      >
        <Loader2 className="h-3 w-3 animate-spin" /> Loading the trend…
      </p>
    );
  }

  if (query.isError) {
    return (
      <p data-testid={`trend-${metric.id}`} className={`${note} text-red-700`}>
        {trendFailure(query.error)}
      </p>
    );
  }

  // Defence in depth against a body that says `success` and carries no series:
  // reading through `data` would throw in render, and taking it for an empty
  // array would file a broken response under "nothing has happened yet".
  const points = query.data?.data?.points;

  if (!Array.isArray(points)) {
    return (
      <p data-testid={`trend-${metric.id}`} className={`${note} text-red-700`}>
        {MALFORMED_TREND}
      </p>
    );
  }

  if (points.length === 0) {
    return (
      <p data-testid={`trend-${metric.id}`} className={`${note} text-gray-500`}>
        No day has been snapshotted for this metric yet. There is no history to
        draw, which is not the same as a history of nothing.
      </p>
    );
  }

  const shape = readTrend(points, query.data?.meta);

  if (!shape.enough) {
    return (
      <div
        data-testid={`trend-${metric.id}`}
        className={`${note} text-gray-600`}
      >
        <p>
          Too few readings for a trend: {plural(shape.measured, "measured day")}{" "}
          of the {MIN_TREND_POINTS} this screen requires. Nothing is drawn from
          them.
        </p>
        {breaksSentence(shape) && (
          <p className="mt-0.5 text-gray-500">{breaksSentence(shape)}</p>
        )}
        {unrecordedSentence(shape) && (
          <p className="mt-0.5 text-gray-500">{unrecordedSentence(shape)}</p>
        )}
      </div>
    );
  }

  const breaks = breaksSentence(shape);
  const missing = unrecordedSentence(shape);
  const change =
    shape.changeIndex !== undefined ? shape.days[shape.changeIndex] : undefined;

  return (
    <figure data-testid={`trend-${metric.id}`} className="mt-2">
      <Sparkline shape={shape} metric={metric} />
      <figcaption className="mt-1 space-y-0.5 text-xs text-gray-500">
        <p>
          {shape.measured} of {plural(shape.span, "day")} measured · high{" "}
          {formatValue(shape.max, metric.unit)} · low{" "}
          {formatValue(shape.min, metric.unit)}
        </p>
        {breaks && <p>{breaks}</p>}
        {missing && <p>{missing}</p>}
        {change && (
          <p className="text-gray-700">
            Definition changed on {change.definedOn} — the days before the rule
            measure something else.
          </p>
        )}
        {shape.changedAfterSeries && (
          <p className="text-gray-700">
            The definition changed after every day shown was recorded, so none
            of this was computed the way the figure above it was.
          </p>
        )}
      </figcaption>
    </figure>
  );
}
